export interface LoanDetailsFieldDef {
  title: string
  /** When false, field is hidden in the floater. Default true. */
  visible?: boolean
  /** builtin = existing Loan Details values; report = free-report placeholder; loanField = Loan Structure field */
  source?: 'builtin' | 'report' | 'loanField'
  /** Report placeholder key (e.g. "Loan #", "Broker-Name") when source is report */
  reportKey?: string
  /** Loan Structure field key (e.g. "productType") when source is loanField */
  fieldKey?: string
}

export interface LoanDetailsSection {
  id: string
  title: string
  fields: string[]
}

export interface LoanDetailsFieldConfig {
  sections: LoanDetailsSection[]
  fields: Record<string, LoanDetailsFieldDef>
}

export const REPORT_FIELD_PREFIX = 'rpt:'
export const LOAN_FIELD_PREFIX = 'lf:'

export const toReportFieldKey = (placeholder: string) => `${REPORT_FIELD_PREFIX}${placeholder}`

export const toLoanFieldConfigKey = (loanFieldKey: string) => `${LOAN_FIELD_PREFIX}${loanFieldKey}`

export const getReportPlaceholderKey = (fieldKey: string, def?: LoanDetailsFieldDef): string | null => {
  if (def?.source === 'report') return def.reportKey || fieldKey.replace(new RegExp(`^${REPORT_FIELD_PREFIX}`), '')
  if (fieldKey.startsWith(REPORT_FIELD_PREFIX)) return fieldKey.slice(REPORT_FIELD_PREFIX.length)
  return null
}

export const getLoanStructureFieldKey = (configKey: string, def?: LoanDetailsFieldDef): string | null => {
  if (def?.source === 'loanField') return def.fieldKey || configKey.replace(new RegExp(`^${LOAN_FIELD_PREFIX}`), '')
  if (configKey.startsWith(LOAN_FIELD_PREFIX)) return configKey.slice(LOAN_FIELD_PREFIX.length)
  return null
}

/** Stable keys used by loanDetails.tsx value map. */
export const LOAN_DETAILS_FIELD_KEYS = [
  'firstName',
  'lastName',
  'tierLevel',
  'entityName',
  'entityState',
  'borrowerType',
  'ssn',
  'dob',
  'creditScore',
  'nonPersonEntity',
  'street',
  'citizenship',
  'firstTimeHomeBuyer',
  'maritalStatus',
  'preferredLanguage',
  'workPhone',
  'email',
  'propertyAddress',
  'propertyType',
  'numberOfUnits',
  'occupancyType',
  'loanPurpose',
  'loanProgramName',
  'amortizationType',
  'loanTerm',
  'interestRate',
  'interestOnly',
  'productType',
  'purchasePrice',
  'interestReserve',
  'constructionBudget',
  'constructionAmountFinanced',
  'escrowRepairHoldback',
  'baseLoan',
  'loanWith',
  'initialLoanAmount',
  'asIsAppraisedValue',
  'afterRepairedAppraisedValue',
  'mortgagePayment',
  'ltv',
  'cltv',
  'arvLtv',
  'ltc',
  'trueLtc',
  'ltp',
  'dscr',
  'frontEndDTI',
  'backEndDTI',
  'prepaymentPenalty',
  'cashToClose',
  'requiredReserves',
  'totalRequiredAssets',
  'assets',
  'maxAllowableCashToClose',
  'cashToBorrowerExcess',
  'allowCashOut',
  'allowCashOutPercentage',
  'originationChannel',
  'escrowWaiver',
  'firstTimeHomeBuyerOther',
  'lockExpiration',
  'loanOfficer',
  'brokerCompanyName',
  'brokerStateOfFormation',
  'brokerCompanyType',
  'closingDate',
  'currentMaturityDate',
  'nextPaymentDate',
  'paymentDaysLate',
] as const

export type LoanDetailsFieldKey = typeof LOAN_DETAILS_FIELD_KEYS[number]

const field = (title: string): LoanDetailsFieldDef => ({ title, visible: true, source: 'builtin' })

export const DEFAULT_LOAN_DETAILS_FIELDS: Record<string, LoanDetailsFieldDef> = {
  firstName: field('First Name'),
  lastName: field('Last Name'),
  tierLevel: field('Tier Level'),
  entityName: field('Entity Name'),
  entityState: field('State of Entity'),
  borrowerType: field('Borrower Type'),
  ssn: field('Social Security Number'),
  dob: field('Date of Birth'),
  creditScore: field('Credit Score'),
  nonPersonEntity: field('Non-Person Entity'),
  street: field('Street'),
  citizenship: field('Citizenship'),
  firstTimeHomeBuyer: field('First Time Home Buyer'),
  maritalStatus: field('Marital Status'),
  preferredLanguage: field('Preferred Language'),
  workPhone: field('Work Phone'),
  email: field('Email'),
  propertyAddress: field('Property Address'),
  propertyType: field('Property Type'),
  numberOfUnits: field('Number of Units'),
  occupancyType: field('Occupancy Type'),
  loanPurpose: field('Loan Purpose'),
  loanProgramName: field('Loan Program Name'),
  amortizationType: field('Amortization Type'),
  loanTerm: field('Loan Term'),
  interestRate: field('Interest Rate'),
  interestOnly: field('Interest Only'),
  productType: field('Product Type'),
  purchasePrice: field('Purchase Price'),
  interestReserve: field('Interest Reserve'),
  constructionBudget: field('Construction Budget'),
  constructionAmountFinanced: field('Construction Amount Financed'),
  escrowRepairHoldback: field('Escrow Repair Holdback'),
  baseLoan: field('Base Loan'),
  loanWith: field('Loan With'),
  initialLoanAmount: field('Initial Loan Amount'),
  asIsAppraisedValue: field('As Is Appraised Value'),
  afterRepairedAppraisedValue: field('After Repaired Appraised Value'),
  mortgagePayment: field('Mortgage Payment'),
  ltv: field('LTV'),
  cltv: field('CLTV'),
  arvLtv: field('ARV LTV'),
  ltc: field('LTC'),
  trueLtc: field('True LTC'),
  ltp: field('LTP'),
  dscr: field('DSCR'),
  frontEndDTI: field('Front-End DTI'),
  backEndDTI: field('Back-End DTI'),
  prepaymentPenalty: field('Prepayment Penalty'),
  cashToClose: field('Cash to Close'),
  requiredReserves: field('Required Reserves'),
  totalRequiredAssets: field('Total Required Assets'),
  assets: field('Assets'),
  maxAllowableCashToClose: field('Max Allowable Cash to Close'),
  cashToBorrowerExcess: field('Cash to Borrower in Excess of reserve requirement'),
  allowCashOut: field('Allow Cash-Out to be used as reserves'),
  allowCashOutPercentage: field('Percentage'),
  originationChannel: field('Origination Channel'),
  escrowWaiver: field('Escrow Waiver'),
  firstTimeHomeBuyerOther: field('1st Time Home Buyer'),
  lockExpiration: field('Lock Expiration'),
  loanOfficer: field('Loan Officer'),
  brokerCompanyName: field('Broker Company Name'),
  brokerStateOfFormation: field('Broker State of Formation'),
  brokerCompanyType: field('Broker Company Type'),
  closingDate: field('Closing Date'),
  currentMaturityDate: field('Current Maturity Date'),
  nextPaymentDate: field('Next Payment Date'),
  paymentDaysLate: field('Days Late'),
}

export const DEFAULT_LOAN_DETAILS_SECTIONS: LoanDetailsSection[] = [
  {
    id: 'borrowerInfo',
    title: 'Borrower Info',
    fields: [
      'firstName',
      'lastName',
      'tierLevel',
      'entityName',
      'entityState',
      'borrowerType',
      'ssn',
      'dob',
      'creditScore',
      'nonPersonEntity',
      'street',
      'citizenship',
      'firstTimeHomeBuyer',
      'maritalStatus',
      'preferredLanguage',
      'workPhone',
      'email',
    ],
  },
  {
    id: 'propertyInfo',
    title: 'Property Info',
    fields: ['propertyAddress', 'propertyType', 'numberOfUnits'],
  },
  {
    id: 'loanPropertyInfo',
    title: 'Loan Property Info',
    fields: [
      'occupancyType',
      'loanPurpose',
      'loanProgramName',
      'amortizationType',
      'loanTerm',
      'interestRate',
      'interestOnly',
    ],
  },
  {
    id: 'loanDetailsInfo',
    title: 'Loan Details Info',
    fields: [
      'productType',
      'purchasePrice',
      'interestReserve',
      'constructionBudget',
      'constructionAmountFinanced',
      'escrowRepairHoldback',
      'baseLoan',
      'loanWith',
      'initialLoanAmount',
      'asIsAppraisedValue',
      'afterRepairedAppraisedValue',
      'mortgagePayment',
      'ltv',
      'cltv',
      'arvLtv',
      'ltc',
      'trueLtc',
      'ltp',
      'dscr',
      'frontEndDTI',
      'backEndDTI',
      'prepaymentPenalty',
      'cashToClose',
      'requiredReserves',
      'totalRequiredAssets',
      'assets',
      'maxAllowableCashToClose',
      'cashToBorrowerExcess',
      'allowCashOut',
      'allowCashOutPercentage',
    ],
  },
  {
    id: 'otherLoanInfo',
    title: 'Other Loan Info',
    fields: [
      'originationChannel',
      'escrowWaiver',
      'firstTimeHomeBuyerOther',
      'lockExpiration',
      'loanOfficer',
      'brokerCompanyName',
      'brokerStateOfFormation',
      'brokerCompanyType',
      'closingDate',
    ],
  },
]

export const DEFAULT_LOAN_DETAILS_FIELD_CONFIG: LoanDetailsFieldConfig = {
  sections: DEFAULT_LOAN_DETAILS_SECTIONS,
  fields: DEFAULT_LOAN_DETAILS_FIELDS,
}

/** Merge saved config with defaults so new fields appear over time. */
export function normalizeLoanDetailsFieldConfig(
  saved?: Partial<LoanDetailsFieldConfig> | null,
): LoanDetailsFieldConfig {
  if (!saved?.sections?.length) {
    return {
      sections: DEFAULT_LOAN_DETAILS_SECTIONS.map((s) => ({ ...s, fields: [...s.fields] })),
      fields: { ...DEFAULT_LOAN_DETAILS_FIELDS },
    }
  }

  const fields: Record<string, LoanDetailsFieldDef> = { ...DEFAULT_LOAN_DETAILS_FIELDS }
  Object.entries(saved.fields || {}).forEach(([key, def]) => {
    if (!def) return
    const isReport = def.source === 'report' || key.startsWith(REPORT_FIELD_PREFIX)
    const isLoanField = def.source === 'loanField' || key.startsWith(LOAN_FIELD_PREFIX)
    fields[key] = {
      title:
        def.title ||
        fields[key]?.title ||
        def.reportKey ||
        def.fieldKey ||
        key.replace(new RegExp(`^${REPORT_FIELD_PREFIX}|^${LOAN_FIELD_PREFIX}`), ''),
      visible: def.visible !== false,
      source: isReport ? 'report' : isLoanField ? 'loanField' : def.source || fields[key]?.source || 'builtin',
      ...(isReport ? { reportKey: def.reportKey || key.replace(new RegExp(`^${REPORT_FIELD_PREFIX}`), '') } : {}),
      ...(isLoanField ? { fieldKey: def.fieldKey || key.replace(new RegExp(`^${LOAN_FIELD_PREFIX}`), '') } : {}),
    }
  })

  const knownKeys = new Set(Object.keys(fields))
  const sections: LoanDetailsSection[] = saved.sections.map((s) => ({
    id: s.id || `section_${Date.now()}`,
    title: s.title || 'Section',
    fields: (s.fields || []).filter((k) => knownKeys.has(k)),
  }))

  // Drop duplicate field placement across sections (first wins)
  const placed = new Set<string>()
  sections.forEach((section) => {
    section.fields = section.fields.filter((k) => {
      if (placed.has(k)) return false
      placed.add(k)
      return true
    })
  })

  // Saved configs can predate newly introduced builtin fields. Append any missing default
  // fields to their default sections so new rows become visible without admin reconfiguration.
  const defaultSectionByField = new Map<string, string>()
  DEFAULT_LOAN_DETAILS_SECTIONS.forEach((section) => {
    section.fields.forEach((key) => defaultSectionByField.set(key, section.id))
  })
  const sectionIndexById = new Map<string, number>()
  sections.forEach((section, index) => sectionIndexById.set(section.id, index))

  DEFAULT_LOAN_DETAILS_SECTIONS.forEach((defaultSection) => {
    defaultSection.fields.forEach((key) => {
      const def = fields[key]
      if (!def || (def.source || 'builtin') !== 'builtin' || placed.has(key)) return

      const targetSectionId = defaultSectionByField.get(key) || defaultSection.id
      const existingSectionIndex = sectionIndexById.get(targetSectionId)
      if (existingSectionIndex !== undefined) {
        sections[existingSectionIndex].fields.push(key)
      } else {
        sections.push({
          id: defaultSection.id,
          title: defaultSection.title,
          fields: [key],
        })
        sectionIndexById.set(defaultSection.id, sections.length - 1)
      }
      placed.add(key)
    })
  })

  // Days Late qualifies the Next Payment Date rather than standing on its own, so a tenant that
  // already shows that date gets it alongside without reconfiguring anything. Deliberately not in a
  // default section: a panel that does not show payment dates has no use for it, and the admin
  // screen still offers it in the unused list for a tenant that wants it elsewhere.
  if (!placed.has('paymentDaysLate')) {
    const host = sections.find((section) => section.fields.includes('nextPaymentDate'))
    if (host) {
      host.fields.splice(host.fields.indexOf('nextPaymentDate') + 1, 0, 'paymentDaysLate')
      placed.add('paymentDaysLate')
    }
  }

  return { sections, fields }
}

export function getUnusedLoanDetailsFields(config: LoanDetailsFieldConfig): string[] {
  const used = new Set(config.sections.flatMap((s) => s.fields))
  return Object.keys(config.fields).filter((k) => {
    if (used.has(k)) return false
    const def = config.fields[k]
    return (def?.source || 'builtin') === 'builtin'
  })
}

/** Report placeholder headers currently assigned & visible in the floater config. */
export function getConfiguredReportHeaders(config: LoanDetailsFieldConfig): string[] {
  const used = new Set(config.sections.flatMap((s) => s.fields))
  const headers: string[] = []
  used.forEach((key) => {
    const def = config.fields[key]
    if (!def || def.visible === false) return
    const reportKey = getReportPlaceholderKey(key, def)
    if (reportKey) headers.push(reportKey)
  })
  return headers
}
